/**
 * Labstep
 *
 * @module grid/NewIndex/coldefs/metadata/molecule/safety
 * @desc Column Definition for metadata molecule safety
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import MoleculeSafety from 'labstep-web/components/Molecule/Safety';
import { MoleculeSafetyPictograms as Pictograms } from 'labstep-web/components/Molecule/Safety/Pictograms';
import { fieldName } from 'labstep-web/components/PubChemMetadata/Form/fields';
import { ICONS } from 'labstep-web/constants/icons';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellEditorParams,
  CellRendererParams,
} from 'labstep-web/core/DataGrid/types';
import {
  createOrUpdateValue,
  setValue,
  stopEditingAndFocus,
} from 'labstep-web/core/DataGrid/utils';
import { TextPlaceholder } from 'labstep-web/core/Text/Placeholder';
import { getMetadata } from 'labstep-web/grid/NewIndex/coldefs';
import type { NewIndexColDef } from 'labstep-web/grid/NewIndex/coldefs/types';
import { GridNewIndexColumnHeader } from 'labstep-web/grid/NewIndex/components/ColumnHeader';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { GridColumnDefinitionService } from 'labstep-web/grid/services/grid-column-definition.service';
import type { Entity } from 'labstep-web/models/entity.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';
import { PubchemService } from 'labstep-web/services/pubchem.service';

export const getMetadataMoleculeSafetyColDef = (
  parentEntityClass: string,
  type: MetadataType,
  metadataLabel: string,
  entityName?: string,
): NewIndexColDef => {
  return {
    colId: `metadata:${parentEntityClass}:${type}:${metadataLabel}:safety`,
    sortable: true,
    headerName: 'Safety',
    headerComponent: GridNewIndexColumnHeader,
    headerComponentParams: {
      icon: ICONS.metadata.type.molecule,
      postFilterSettings: {
        path:
          parentEntityClass !== entityName
            ? `${parentEntityClass}.metadatas`
            : 'metadatas',
      },
    },
    editable: (params: EditableCallbackParams) => {
      const entity = params.node.data;
      if (
        parentEntityClass === 'purchase_order' &&
        !entity.purchase_order
      ) {
        return false;
      }
      return GridNewIndexService.getEditable(params);
    },
    valueGetter: (params: ValueGetterParams): string | null => {
      // const entity = getEntity(params);
      // if (entity instanceof Molecule && entity.properties) {
      //   return JSON.stringify(entity.properties.Safety);
      // }
      return null;
    },
    valueSetter: (params: ValueSetterParams): boolean => {
      // FIXME
      return false;
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }
      const entity = params.node.data;
      if (
        parentEntityClass === 'purchase_order' &&
        !entity.purchase_order
      ) {
        return null;
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      const metadataThreadId =
        parentEntityClass !== params.node.data.entityName
          ? params.node.data[parentEntityClass].metadata_thread.idAttr
          : params.node.data.metadata_thread.idAttr;

      if (!metadata) {
        if (
          parentEntityClass !== params.node.data.entityName &&
          !params.node.data[parentEntityClass]
        ) {
          return false;
        }
        const props = {
          parentName: MetadataThread.entityName,
          parentId: metadataThreadId,
        };
        return (
          <TextPlaceholder
            onClick={() => {
              createOrUpdateValue({
                entityName: Metadata.entityName,
                body: {
                  type,
                  label: metadataLabel,
                  metadata_thread_id: metadataThreadId,
                  molecule: { name: 'Untitled structure' },
                },
                createProps: props,
                options:
                  GridColumnDefinitionService.getOptions(params),
              });
              return true;
            }}
          >
            Create structure
          </TextPlaceholder>
        );
      }

      if (!metadata.molecule.properties) {
        return (
          <DataGridPlaceholder
            params={params}
            editable={GridNewIndexService.getEditable(params)}
            placeholder="Enter"
          />
        );
      }

      if (
        metadata.molecule.properties.Safety.pictograms?.length === 0
      ) {
        return (
          <DataGridPlaceholder
            params={params}
            editable={GridNewIndexService.getEditable(params)}
            placeholder="Enter"
          />
        );
      }

      return (
        <Pictograms
          pubChemMetadata={metadata.molecule.properties}
          isInline
          size="mini"
        />
      );
    },
    cellEditorPopup: true,
    cellEditor: (params: CellEditorParams<Entity>) => {
      if (!params.node) {
        return '';
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (!metadata) {
        return null;
      }

      if (metadata.molecule && !metadata.molecule.properties) {
        setValue(
          'pubchem',
          PubchemService.getPubchemBody(
            metadata.molecule,
            fieldName,
            '',
          ),
          {
            entityName: metadata.molecule.entityName,
            id: metadata.molecule.idAttr,
          },
        );
        return null;
      }
      return (
        <MoleculeSafety
          parent={metadata?.molecule}
          pubChemMetadata={metadata?.molecule?.properties}
          modalProps={{
            open: true,
            onClose: () => stopEditingAndFocus(params),
            viewComponent: null,
          }}
          metadataId={metadata.idAttr}
        />
      );
    },
  };
};
